package com.essntl.features.proposal.ui.allproposal.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import com.essntl.features.proposal.ui.allproposal.AllProposalEvent
import com.essntl.features.proposal.ui.allproposal.AllProposalNavigation
import com.essntl.features.proposal.ui.allproposal.AllProposalState
import com.essntl.uikit.components.button.outline.EsMediumOutlineButton
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.key.upDownSelection
import com.essntl.uikit.rememberEquals
import com.essntl.uikit.theme.esColorScheme
import kotlin.coroutines.cancellation.CancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

@Composable
fun ProposalListColumn(
    state: AllProposalState,
    onEvent: (AllProposalEvent) -> Unit,
    onNavigation: (AllProposalNavigation) -> Unit,
    onAddProposal: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val lazyListState = rememberLazyListState()
    val scope = rememberCoroutineScope()

    Box(
        contentAlignment = Alignment.Center,
        modifier = modifier
            .fillMaxSize()
            .clip(RoundedCornerShape(EsConstants.RoundedCornerSize))
            .background(MaterialTheme.esColorScheme.bgSecondary),
    ) {
        if (state.getAllProposal.data.isEmpty() &&
            state.getAllProposal.isLoading
        ) {
            EsSpinner()
        } else if (
            state.getAllProposal.isFailure &&
            state.getAllProposal.error !is CancellationException
        ) {
            AllProposalError(
                onTryAgain = {
                    onEvent(AllProposalEvent.GetAllProposal)
                },
            )
        } else if (state.getAllProposal.data.isEmpty()) {
            AllProposalEmpty(
                onAddProposal = onAddProposal,
            )
        } else {
            LazyColumn(
                verticalArrangement = Arrangement.spacedBy(EsConstants.ListRowVerticalSpacing),
                horizontalAlignment = Alignment.CenterHorizontally,
                contentPadding = PaddingValues(
                    vertical = EsConstants.SectionVerticalPadding,
                ),
                state = lazyListState,
                modifier = Modifier
                    .fillMaxSize(),
            ) {
                itemsIndexed(state.getAllProposal.data, key = { _, p -> p.id }) { index, proposal ->
                    val isUpdateProposalOwnerLoading =
                        rememberEquals(proposal.id, state.updateProposalOwnerLoading.toSet()) { it }

                    val isSelected = rememberEquals(proposal.id, setOf(state.selectedProposalId)) { it }

                    val focusRequester = remember { FocusRequester() }

                    LaunchedEffect(isSelected) {
                        delay(100L)
                        if (isSelected)
                            focusRequester.requestFocus()
                    }

                    ProposalItem(
                        state = state,
                        onEvent = onEvent,
                        onNavigation = onNavigation,
                        index = index,
                        proposal = proposal,
                        isSelected = isSelected,
                        isUpdateProposalOwnerLoading = isUpdateProposalOwnerLoading,
                        modifier = Modifier
                            .upDownSelection(
                                focusRequester = focusRequester,
                                isSelected = isSelected,
                                list = state.getAllProposal.data,
                                index = index,
                                onSelect = {
                                    onNavigation(AllProposalNavigation.Proposal(it.id))
                                },
                                lazyListState = lazyListState,
                                scrollTo = { scope.launch { lazyListState.animateScrollToItem(it) } },
                            )
                            .padding(
                                horizontal = EsConstants.SectionHorizontalPadding,
                            ),
                    )
                }

                if (!state.getAllProposal.isLastPage) {
                    item {
                        Spacer(modifier = Modifier.height(2.spacing))
                        EsMediumOutlineButton(
                            onClick = {
                                onEvent(AllProposalEvent.LoadMoreProposals)
                            },
                            text = "Load more",
                            loading = state.getAllProposal.isLoading,
                            modifier = Modifier,
                        )
                        Spacer(modifier = Modifier.height(2.spacing))
                    }
                }
            }
        }
    }
}